import { Box, IconButton, TextField, Typography } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { Material } from "../../models/Material";
import {
  Check,
  CheckIcon,
  Minus,
  PencilSimple,
  PencilSimpleIcon,
  Plus,
  PlusIcon,
  TrashSimple,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import { useMaterial } from "../../stores/useMaterial";
import { useProfile } from "../../stores/useProfile";
import { useMessage } from "../messagePoppup/MessageContext";
import { useCrate } from "../../stores/useCrate";

type MaterialItemProps = {
  material: Material;
  crateId?: number;
  onSave: () => void;
};

export const MaterialItem = ({
  material,
  crateId,
  onSave,
}: MaterialItemProps) => {
  const [editId, setEditId] = useState<number | undefined>();
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [editedValues, setEditedValues] = useState<Partial<Material>>({});
  const { editMaterial, removeMaterial } = useMaterial();
  const { removeMaterialFromCrate } = useCrate();
  const { user } = useProfile();
  const { showMessage } = useMessage();

  const handleEdit = (material: Material) => {
    setEditId(material.id);
    setEditedValues(material);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setEditedValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    if (!user || !editId) return;
    const response = await editMaterial(user, editedValues as Material, editId);
    showMessage({ statusCode: response });
    setEditId(undefined);
    onSave();
  };

  const handleRemove = async () => {
    if (!user || !material.id) return;

    const response = crateId
      ? await removeMaterialFromCrate(user, crateId, material.id)
      : await removeMaterial(user, material.id);
    showMessage({ statusCode: response });
    setConfirmation(false);
    onSave();
  };

  const stockChange = async (change: number) => {
    if (!user || !material.id) return;

    const updatedMaterial = {
      ...material,
      stock: (material.stock || 0) + change,
    };

    const response = await editMaterial(user, updatedMaterial, material.id);
    showMessage({ statusCode: response });
    onSave();
  };

  return (
    <Box key={material.id} className="row">
      {editId === material.id ? (
        <>
          <Typography variant="h4" className="id">
            {material.id}
          </Typography>
          <Box className="brand">
            <TextField
              id="brand"
              label="Brand"
              fullWidth
              variant="outlined"
              value={editedValues.brand}
              onChange={handleChange}
              InputProps={{ disableUnderline: true }}
            />
          </Box>
          <Box className="material">
            <TextField
              id="name"
              label="Material"
              fullWidth
              variant="outlined"
              value={editedValues.name}
              onChange={handleChange}
              InputProps={{ disableUnderline: true }}
            />
          </Box>
          {!crateId && (
            <>
              <Box className="netprice">
                <TextField
                  id="netPrice"
                  label="Net (€)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={editedValues.netPrice}
                  onChange={handleChange}
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
              <Box className="grossprice">
                <TextField
                  id="grossPrice"
                  label="Gross (€)"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={editedValues.grossPrice}
                  onChange={handleChange}
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
              <Box className="stock">
                <TextField
                  id="stock"
                  label="Stock"
                  variant="outlined"
                  fullWidth
                  size="small"
                  onChange={handleChange}
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </>
          )}

          <Box className="actions">
            <IconButton onClick={handleSave}>
              <Check size={24} weight="bold" />
            </IconButton>
          </Box>
        </>
      ) : (
        <>
          {!crateId && (
            <Typography variant="h4" className="id">
              {material.id}
            </Typography>
          )}
          <Typography variant="h4" className="brand">
            {material.brand}
          </Typography>
          <Typography variant="h4" className="material">
            {material.name}
          </Typography>
          {!crateId && (
            <>
              <Typography variant="h4" className="netprice">
                €{Number(material.netPrice).toFixed(2)}
              </Typography>
              <Typography variant="h4" className="grossprice">
                €{Number(material.grossPrice).toFixed(2)}
              </Typography>
            </>
          )}
          <Box className="stock">
            <Typography variant="h4">{Number(material.stock)}</Typography>
            <Box className="buttonWrapper">
              <IconButton onClick={() => stockChange(1)}>
                <PlusIcon size={24} weight="bold" />
              </IconButton>
              <IconButton
                onClick={() => {
                  stockChange(-1);
                }}>
                <Minus size={24} weight="bold" />
              </IconButton>
            </Box>
          </Box>
          <Box className="actions">
            {confirmation ? (
              <IconButton onClick={handleRemove}>
                <CheckIcon size={24} weight="bold" />
              </IconButton>
            ) : (
              <>
                {!crateId && (
                  <IconButton onClick={() => handleEdit(material)}>
                    <PencilSimpleIcon size={24} weight="bold" />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => {
                    setConfirmation(true);
                  }}>
                  <TrashSimpleIcon size={24} weight="bold" />
                </IconButton>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
